/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {HtmlPlayer} from '@amblique/base/app/plugins/video/adapters/html-player';
import {Player} from '@amblique/base/app/plugins/video/adapters/player';
import {AbstractVideo} from '@amblique/base/app/plugins/video/providers/abstract-video';

/**
 * HTML5 Video Provider
 *
 * Supports native HTML5 video playback with absolute URLs.
 * Accepts video URLs for common formats: mp4, webm, ogg, etc.
 *
 * @example
 * <Video id="https://example.com/video.mp4" />
 * <Video id="/assets/video.mp4" />
 */
export class Html extends AbstractVideo {
  static TYPE = 'html';
  static PLAYER = HtmlPlayer;
  static PARAMS = ['autoplay', 'controls', 'crossorigin', 'height', 'loop', 'muted', 'playsinline', 'poster', 'preload', 'width'];
  static EVENTS = ['play', 'playing', 'pause', 'ended', 'timeupdate', 'progress', 'seeking', 'seeked', 'volumechange', 'ratechange', 'loadstart', 'loadeddata', 'loadedmetadata', 'canplay', 'canplaythrough', 'error', 'stalled', 'waiting'];

  // Match and capture the entire URL/path for video files
  // Supports: absolute URLs (http/https), relative URLs (/ or ./), data URLs, blob URLs
  static ID_PATTERN = /^((?:https?:\/\/|\/|\.\/|data:|blob:).+\.(?:mp4|webm|ogg|mov|avi|mkv|m4v)(?:\?.*)?)$/i;

  // Match video file extensions or URLs containing common video formats
  static TYPE_PATTERN = /\.(?:mp4|webm|ogg|mov|avi|mkv|m4v)(?:\?|$)|^https?:\/\/|^\/|^data:|^blob:/i;

  /**
   * For HTML5 video, the ID is the video URL itself
   * No oEmbed API needed - we create metadata from video element properties
   *
   * @protected
   */
  get oembedUrl() {
    return null;
  }

  /**
   * Override loadMetadata to skip oEmbed API call
   * For HTML5 video, we'll create metadata from the video element after it loads
   *
   * @protected
   * @returns {Promise<Metadata>}
   */
  async loadMetadata() {
    // If already loaded, return cached metadata
    if (this.metadata) {
      return Promise.resolve(this.metadata);
    }

    // If loading in progress, return existing promise
    if (this.metadataPromise) {
      return this.metadataPromise;
    }

    // For HTML5, we need to create a temporary video element to get metadata
    if (!this.isDestroyed()) {
      this.metadataPromise = new Promise((resolve, reject) => {
        const video = document.createElement('video');

        video.preload = 'metadata';
        video.src = this.id;
        video.crossOrigin = this.options.params.crossorigin || 'anonymous';

        const onLoadedMetadata = () => {
          const metadata = {
            type: 'video',
            version: '1.0',
            title: this.id.split('/').pop().split('.')[0],
            author_name: '',
            author_url: '',
            provider_name: 'HTML5 Video',
            provider_url: '',
            thumbnail_url: this.options.params.poster || '',
            html: '', // Will be generated in createPlayer
            width: video.videoWidth || 16,
            height: video.videoHeight || 9,
            duration: video.duration,
          };

          // Clean up temporary video element
          video.removeEventListener('loadedmetadata', onLoadedMetadata);
          video.removeEventListener('error', onError);
          video.src = '';

          resolve(this.onMetadataLoaded(metadata));
        };

        const onError = (error) => {
          video.removeEventListener('loadedmetadata', onLoadedMetadata);
          video.removeEventListener('error', onError);
          video.src = '';

          reject(new Error(`Failed to load video metadata: ${error.message || 'Unknown error'}`));
        };

        video.addEventListener('loadedmetadata', onLoadedMetadata);
        video.addEventListener('error', onError);
      })
        .catch((error) => {
          this.handleError(error);
          throw error;
        })
        .finally(() => delete this.metadataPromise)
      ;

      return this.metadataPromise;
    }

    return Promise.resolve(this.metadata);
  }

  /**
   * Create new HTML5 video player instance
   *
   * @protected
   *
   * @param {object} metadata Video metadata object
   *
   * @returns {Player} Returns new HTML5 player instance
   */
  createPlayer(metadata) {
    const video = document.createElement('video');

    // Apply parameters as attributes
    const params = this.options.params;

    if (params.autoplay) video.autoplay = true;
    if (params.controls) video.controls = true;
    if (params.crossorigin) video.crossOrigin = params.crossorigin;
    if (params.loop) video.loop = true;
    if (params.muted) video.muted = true;
    // Default inline playback on iOS Safari; opt out with playsinline: false
    video.playsInline = params.playsinline !== false;
    if (params.poster) video.poster = params.poster;
    if (params.preload) video.preload = params.preload;

    // Set source
    video.src = this.id;

    // Apply styling for proper video display
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.objectFit = this.options.size || 'cover';
    video.style.position = 'absolute';
    video.style.top = '0';
    video.style.left = '0';

    // Apply clipping if needed
    this.applyClip(video);

    video.addEventListener('loadedmetadata', () => {
      video.dispatchEvent(new Event('load'));
    }, {once: true});

    // Add to container
    this.element.appendChild(video);

    // Create provider-specific player adapter
    const htmlPlayerAdapter = new HtmlPlayer(video, this.options);

    // Wrap in unified Player
    this.player = new Player(htmlPlayerAdapter);

    return this.player;
  }

  /**
   * Register video events configured in options
   *
   * @protected
   *
   * @param {Player} player The HTML5 player instance
   *
   * @returns {Player} Returns HTML5 player instance
   */
  registerEvents(player) {
    super.registerEvents(player);

    // HTML5 video is ready immediately after creation
    if (typeof this.options.events.onReady === 'function') {
      Promise.resolve().then(() => this.options.events.onReady(this));
    }

    return player;
  }
}
